
import * as THREE from "../../libs/three.js/build/three.module.js";
import {MOUSE} from "../defines.js";
import {Utils} from "../utils.js";
import {EventDispatcher} from "../EventDispatcher.js";
import {attachFreezeDuringTween} from "./ZoomFlyAnimation.js";

export class EarthControls extends EventDispatcher {
	constructor (viewer) {
		super(viewer);

		this.viewer = viewer;
		this.renderer = viewer.renderer;

		this.scene = null;
		this.sceneControls = new THREE.Scene();

		this.rotationSpeed = 10;

		this.fadeFactor = 20;
		this.wheelDelta = 0;
		this.zoomDelta = new THREE.Vector3();
		this.camStart = null;
		this.panForward = new THREE.Vector3();
		this.navLayerObject = null;
		this.navLayerScale = 0;
		this.navLayerLockUntil = 0;
		this.wheelBurstMs = 300;

		this.tweens = [];

		{
			let sg = new THREE.SphereGeometry(0.7, 16, 16);
			let sm = new THREE.MeshStandardMaterial({ color: 0xffff24 });;
			this.pivotIndicator = new THREE.Mesh(sg, sm);
			this.pivotIndicator.visible = false;
			this.pivotIndicator.castShadow = true;
			this.sceneControls.add(this.pivotIndicator);

			let light = new THREE.DirectionalLight(0xffffff, 1);
			light.position.set(1, 1, 1);
			light.castShadow = true; // 启用光源的阴影投射
			this.sceneControls.add(light);
		}

		let drag = (e) => {
			if (e.drag.object !== null) {
				return;
			}


			if (e.drag.startHandled === undefined) {
				e.drag.startHandled = true;

				this.dispatchEvent({type: 'start'});
			}

			let camStart = this.camStart;
			let camera = this.scene.getActiveCamera();
			let view = this.viewer.scene.view;

			// let camera = this.viewer.scene.camera;
			let mouse = e.drag.end;
			let domElement = this.viewer.renderer.domElement;

			if (e.drag.mouse === MOUSE.RIGHT) {
				if (this.pivot && camStart) {
					camStart.updateMatrixWorld(true);
					let ray = Utils.mouseToRay(mouse, camStart, domElement.clientWidth, domElement.clientHeight);
					const forward = this.panForward.lengthSq() > 0
						? this.panForward
						: camStart.getWorldDirection(new THREE.Vector3());
					let plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
						forward.clone().normalize(),
						this.pivot);

					let distanceToPlane = ray.distanceToPlane(plane);

					if (Number.isFinite(distanceToPlane) && distanceToPlane > 0) {
						let I = ray.origin.clone().add(
							ray.direction.clone().multiplyScalar(distanceToPlane));
						const panOffset = new THREE.Vector3().subVectors(I, this.pivot);
						view.position.copy(camStart.position).sub(panOffset);
					}
				} else {
					let ndrag = {
						x: e.drag.lastDrag.x / domElement.clientWidth,
						y: e.drag.lastDrag.y / domElement.clientHeight
					};

					const radius = Number.isFinite(view.radius) ? Math.abs(view.radius) : 0;
					let panSpeed = Math.max(radius, this.navLayerScale || 0) * 1.5;
					let panOffset = new THREE.Vector3();

					let up = new THREE.Vector3(0, 1, 0);
					let right = new THREE.Vector3(1, 0, 0);
					right.applyQuaternion(camera.quaternion);
					up.applyQuaternion(camera.quaternion);

					panOffset.add(right.multiplyScalar(-ndrag.x * panSpeed));
					panOffset.add(up.multiplyScalar(ndrag.y * panSpeed));

					view.position.add(panOffset);
				}
			} else if (e.drag.mouse === MOUSE.LEFT) {
				let ndrag = {
					x: e.drag.lastDrag.x / this.renderer.domElement.clientWidth,
					y: e.drag.lastDrag.y / this.renderer.domElement.clientHeight
				};

				if (!this.pivot) {
					this.pivot = this.getEmptyClickPivot();
				}

				let yawDelta = -ndrag.x * this.rotationSpeed * 0.5;
				let pitchDelta = -ndrag.y * this.rotationSpeed * 0.2;

				let originalPitch = view.pitch;
				let tmpView = view.clone();
				tmpView.pitch = tmpView.pitch + pitchDelta;
				pitchDelta = tmpView.pitch - originalPitch;

				let pivotToCam = new THREE.Vector3().subVectors(view.position, this.pivot);
				let pivotToCamTarget = new THREE.Vector3().subVectors(view.getPivot(), this.pivot);
				let side = view.getSide();

				pivotToCam.applyAxisAngle(side, pitchDelta);
				pivotToCamTarget.applyAxisAngle(side, pitchDelta);

				pivotToCam.applyAxisAngle(new THREE.Vector3(0, 0, 1), yawDelta);
				pivotToCamTarget.applyAxisAngle(new THREE.Vector3(0, 0, 1), yawDelta);

				let newCam = new THREE.Vector3().addVectors(this.pivot, pivotToCam);
				// TODO: Unused: let newCamTarget = new THREE.Vector3().addVectors(this.pivot, pivotToCamTarget);

				view.position.copy(newCam);
				view.yaw += yawDelta;
				view.pitch += pitchDelta;
			}
		};

		let onMouseDown = e => {
			const isRight = e.button === 2 || (e.buttons & MOUSE.RIGHT);
			if (isRight) {
				this.applyRightNavigationPivot(e.mouse);
				return;
			}

			let I = Utils.getMouseSceneIntersection(
				e.mouse,
				this.scene.getActiveCamera(),
				this.viewer,
				this.scene.pointclouds,
				{pickClipped: false});

			if (I) {
				this.pivot = I.location;
				this.camStart = this.scene.getActiveCamera().clone();
				this.pivotIndicator.visible = true;
				this.pivotIndicator.position.copy(I.location);
				Utils.notifyClickPointCloud(I.pointcloud || null);
			} else {
				this.pivot = this.getEmptyClickPivot();
				this.camStart = this.scene.getActiveCamera().clone();
				this.pivotIndicator.visible = true;
				this.pivotIndicator.position.copy(this.pivot);
				Utils.notifyClickPointCloud(null);
			}
		};

		let drop = e => {
			this.dispatchEvent({type: 'end'});
		};

		let onMouseUp = e => {
			this.camStart = null;
			this.panForward.set(0, 0, 0);
			this.pivot = null;
			this.pivotIndicator.visible = false;
			document.body.classList.remove('crosshair-cursor'); // 移除十字光标样式
		};

		let scroll = (e) => {
			this.wheelDelta += e.delta;
		};

		let dblclick = (e) => {
			this.zoomToLocation(e.mouse);
		};

		this.addEventListener('drag', drag);
		this.addEventListener('drop', drop);
		this.addEventListener('mousewheel', scroll);
		this.addEventListener('mousedown', onMouseDown);
		this.addEventListener('mouseup', onMouseUp);
		this.addEventListener('dblclick', dblclick);
	}

	setScene (scene) {
		this.scene = scene;
	}

	stop(){
		this.wheelDelta = 0;
		this.zoomDelta.set(0, 0, 0);
	}

	rememberNavLayer (layer) {
		if (!layer || !(layer.object || layer.pointcloud)) {
			return;
		}
		this.navLayerObject = layer.pointcloud || layer.object;
		this.navLayerScale = layer.scale;
		this.navLayerLockUntil = performance.now() + this.wheelBurstMs;
	}

	resolveWheelLayer (mouse, camera) {
		const now = performance.now();
		if (this.navLayerObject && now < this.navLayerLockUntil) {
			return Utils.resolveLockedNavigationLayer(
				mouse, camera, this.viewer, this.navLayerObject);
		}
		return Utils.resolveNavigationLayer(mouse, camera, this.viewer);
	}

	getEmptyClickPivot () {
		const view = this.viewer.scene.view;
		const lookAt = view.getPivot();
		if (lookAt && [lookAt.x, lookAt.y, lookAt.z].every(Number.isFinite) && view.radius > 0.2) {
			return lookAt.clone();
		}
		const dist = Math.max(
			Number.isFinite(view.radius) ? Math.abs(view.radius) : 0,
			this.navLayerScale || 0,
			1
		);
		return view.position.clone().add(view.direction.clone().normalize().multiplyScalar(dist));
	}

	applyRightNavigationPivot (mouse) {
		const camera = this.scene.getActiveCamera();
		const view = this.viewer.scene.view;
		this.camStart = camera.clone();
		this.camStart.updateMatrixWorld(true);
		this.camStart.getWorldDirection(this.panForward);

		const layer = Utils.resolveNavigationLayer(
			mouse, camera, this.viewer, {pickClipped: false});
		this.rememberNavLayer(layer);

		if (layer.hit && !layer.close) {
			this.pivot = layer.hit.location.clone();
			this.pivotIndicator.visible = true;
			this.pivotIndicator.position.copy(this.pivot);
			Utils.notifyClickPointCloud(layer.pointcloud || null);
			return;
		}

		if (layer.object || layer.pointcloud) {
			const radius = Number.isFinite(view.radius) && view.radius > 0.2 ? view.radius : 0;
			const dist = Math.max(layer.scale, radius);
			const dom = this.viewer.renderer.domElement;
			const ray = Utils.mouseToRay(mouse, this.camStart, dom.clientWidth, dom.clientHeight);
			this.pivot = ray.origin.clone().add(ray.direction.clone().multiplyScalar(dist));
			this.pivotIndicator.visible = true;
			this.pivotIndicator.position.copy(this.pivot);
			Utils.notifyClickPointCloud(layer.pointcloud || null);
			return;
		}

		this.pivot = null;
		Utils.notifyClickPointCloud(null);
	}

	zoomToLocation(mouse){
		let camera = this.scene.getActiveCamera();

		let I = Utils.getMouseSceneIntersection(
			mouse,
			camera,
			this.viewer,
			this.scene.pointclouds);

		if (I === null) {
			return;
		}

		let targetRadius = 0;
		{
			let minimumJumpDistance = 0.2;

			let domElement = this.renderer.domElement;
			let ray = Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);

			if (I.pointcloud) {
				let nodes = I.pointcloud.nodesOnRay(I.pointcloud.visibleNodes, ray);
				let lastNode = nodes[nodes.length - 1];
				let radius = lastNode.getBoundingSphere(new THREE.Sphere()).radius;
				targetRadius = Math.min(this.scene.view.radius, radius);
				targetRadius = Math.max(minimumJumpDistance, targetRadius);
			} else {
				// 命中 mesh 等外部对象：没有点云节点可参考，使用当前视口半径兜底
				targetRadius = Math.max(minimumJumpDistance, this.scene.view.radius);
			}
		}

		let d = this.scene.view.direction.multiplyScalar(-1);
		let cameraTargetPosition = new THREE.Vector3().addVectors(I.location, d.multiplyScalar(targetRadius));
		// TODO Unused: let controlsTargetPosition = I.location;

		let animationDuration = 1000;
		let easing = TWEEN.Easing.Quartic.Out;

		{ // animate
			let value = {x: 0};
			let tween = new TWEEN.Tween(value).to({x: 1}, animationDuration);
			tween.easing(easing);
			this.tweens.push(tween);

			let startPos = this.scene.view.position.clone();
			let targetPos = cameraTargetPosition.clone();
			let startRadius = this.scene.view.radius;
			let targetRadius = cameraTargetPosition.distanceTo(I.location);

			tween.onUpdate(() => {
				let t = value.x;
				this.scene.view.position.x = (1 - t) * startPos.x + t * targetPos.x;
				this.scene.view.position.y = (1 - t) * startPos.y + t * targetPos.y;
				this.scene.view.position.z = (1 - t) * startPos.z + t * targetPos.z;

				this.scene.view.radius = (1 - t) * startRadius + t * targetRadius;
				this.viewer.setMoveSpeed(this.scene.view.radius / 2.5);
			});

			attachFreezeDuringTween(this.viewer, tween, () => {
				this.tweens = this.tweens.filter(e => e !== tween);
			});

			tween.start();
		}
	}

	moveToLocation(mouse) {
		let camera = this.scene.getActiveCamera();

		let I = Utils.getMouseSceneIntersection(
			mouse,
			camera,
			this.viewer,
			this.scene.pointclouds);

		if (I === null) {
			return;
		}

		let d = this.scene.view.direction.multiplyScalar(-1);
		let cameraTargetPosition = new THREE.Vector3().addVectors(I.location, d.multiplyScalar(this.scene.view.radius));

		let animationDuration = 1000;
		let easing = TWEEN.Easing.Quartic.Out;

		{ // animate
			let value = { x: 0 };
			let tween = new TWEEN.Tween(value).to({ x: 1 }, animationDuration);
			tween.easing(easing);
			this.tweens.push(tween);

			let startPos = this.scene.view.position.clone();
			let targetPos = cameraTargetPosition.clone();
			let startRadius = this.scene.view.radius;
			let targetRadius = cameraTargetPosition.distanceTo(I.location);

			tween.onUpdate(() => {
				let t = value.x;
				this.scene.view.position.x = (1 - t) * startPos.x + t * targetPos.x;
				this.scene.view.position.y = (1 - t) * startPos.y + t * targetPos.y;
				this.scene.view.position.z = (1 - t) * startPos.z + t * targetPos.z;

				this.scene.view.radius = (1 - t) * startRadius + t * targetRadius;
				this.viewer.setMoveSpeed(this.scene.view.radius / 2.5);
			});

			tween.onComplete(() => {
				this.tweens = this.tweens.filter(e => e !== tween);
			});

			tween.start();
		}
	}

	update (delta) {
		let view = this.scene.view;
		let fade = Math.pow(0.5, this.fadeFactor * delta);
		let progression = 1 - fade;
		let camera = this.scene.getActiveCamera();

		// compute zoom
		if (this.wheelDelta !== 0) {
			const mouse = this.viewer.inputHandler.mouse;
			const layer = this.resolveWheelLayer(mouse, camera);
			this.rememberNavLayer(layer);

			const I = layer.hit && !layer.close ? layer.hit : null;
			if (I) {
				let resolvedPos = new THREE.Vector3().addVectors(view.position, this.zoomDelta);
				let distance = I.location.distanceTo(resolvedPos);
				let jumpDistance = distance * 0.2 * this.wheelDelta;
				let targetDir = new THREE.Vector3().subVectors(I.location, view.position);
				targetDir.normalize();

				resolvedPos.add(targetDir.multiplyScalar(jumpDistance));
				this.zoomDelta.subVectors(resolvedPos, view.position);

				{
					let remain = resolvedPos.distanceTo(I.location);
					view.radius = remain;
					let speed = view.radius / 2.5;
					this.viewer.setMoveSpeed(speed);
				}
			} else if (layer.object || layer.pointcloud) {
				const domElement = this.viewer.renderer.domElement;
				const ray = Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);
				const jumpDistance = layer.scale * this.wheelDelta;
				this.zoomDelta = ray.direction.clone().multiplyScalar(jumpDistance);
				const radius = Number.isFinite(view.radius) ? Math.abs(view.radius) : 0;
				view.radius = Math.max(radius, layer.scale);
				this.viewer.setMoveSpeed(view.radius / 2.5);
			} else {
				let domElement = this.viewer.renderer.domElement;
				let ray = Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);
				let jumpDistance = view.radius * 0.2 * this.wheelDelta;
				this.zoomDelta = ray.direction.clone().multiplyScalar(jumpDistance);
				let speed = view.radius / 2.5;
				this.viewer.setMoveSpeed(speed);
			}
		}

		// apply zoom
		if (this.zoomDelta.length() !== 0) {
			let p = this.zoomDelta.clone().multiplyScalar(progression);

			let newPos = new THREE.Vector3().addVectors(view.position, p);
			view.position.copy(newPos);
		}

		if (this.pivotIndicator.visible) {
			let distance = this.pivotIndicator.position.distanceTo(view.position);
			let pixelwidth = this.renderer.domElement.clientwidth;
			let pixelHeight = this.renderer.domElement.clientHeight;
			let pr = Utils.projectedRadius(1, camera, distance, pixelwidth, pixelHeight);
			let scale = (10 / pr);
			this.pivotIndicator.scale.set(scale, scale, scale);
		}

		// decelerate over time
		{
			this.zoomDelta.multiplyScalar(fade);
			this.wheelDelta = 0;
		}
	}
};
